// useDemandPortfolioSummary — server-engine adapter for the demand-side
// Turnover Control Tower (Sprint 6).
//
// Wraps `get_demand_portfolio_summary(profile_id, start, end)` over the
// [startDate, endDate] window the Control Tower is displaying. PRIVACY: this
// RPC is platform-only and returns `day_flags: []`; the client fallback below
// is computed exclusively from the apartment manager's own platform bookings.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildClientFallbackSummary,
  fetchDemandPortfolioSummary,
  trackSchedulerEngineFallback,
  type ClientFallbackInput,
  type SchedulerError,
  type SchedulerSource,
  type ServerSchedulerSummary,
} from "@/lib/scheduler";
import type { UseSchedulerState } from "./types";

export interface UseDemandPortfolioSummaryOptions {
  profileId: string | null | undefined;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
  clientFallback?: () => ClientFallbackInput;
  enabled?: boolean;
}

export function useDemandPortfolioSummary(
  options: UseDemandPortfolioSummaryOptions,
): UseSchedulerState<ServerSchedulerSummary> {
  const { profileId, startDate, endDate, clientFallback, enabled = true } = options;
  const [data, setData] = useState<ServerSchedulerSummary | null>(null);
  const [source, setSource] = useState<SchedulerSource | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<SchedulerError | null>(null);
  const [tick, setTick] = useState(0);
  const fallbackRef = useRef(clientFallback);
  fallbackRef.current = clientFallback;

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!enabled || !profileId || !startDate || !endDate) {
      setData(null);
      setSource(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      const result = await fetchDemandPortfolioSummary({ profileId, startDate, endDate });
      if (cancelled) return;
      if (result.data) {
        setData(result.data);
        setSource(result.source);
        setError(null);
        setIsLoading(false);
        return;
      }
      const recoverable = !result.error || result.error.recoverable;
      if (recoverable && fallbackRef.current) {
        const fallback = buildClientFallbackSummary(fallbackRef.current());
        setData(fallback);
        setSource("client_fallback");
        setError(null);
        trackSchedulerEngineFallback({
          surface: "demand_control_tower",
          scope: "demand_portfolio",
          reason: result.error?.message ?? "no_data",
          errorCode: result.error?.code,
          recoverable: true,
        });
      } else {
        setData(null);
        setSource(null);
        setError(result.error);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [profileId, startDate, endDate, enabled, tick]);

  return useMemo(
    () => ({ data, source, isLoading, error, refetch }),
    [data, source, isLoading, error, refetch],
  );
}
